import { cx } from "class-variance-authority";

import { RateGaugeGeometry, RateGaugeSize } from "./const";
import * as theme from "./RateGauge.css";

const EMPTY_VALUE = "—";

const START_ANGLE = -90;

// Px from the box center. Value and label straddle it, so neither is centred
// alone — retune a row if that size's font sizes change.
const TEXT_OFFSETS: Record<
    Exclude<RateGaugeSize, "xs">,
    { value: number; label: number }
> = {
    large: { value: -12.75, label: 22 },
    default: { value: -11.5, label: 21.5 },
};

export interface RateGaugeProps {
    /** 0–100, or null when there is nothing to measure. */
    percentage: number | null;
    size?: RateGaugeSize;
    /** Sub-label under the value. Ignored at `xs`, which has no room for it. */
    label?: string;
    /** Arc color. Defaults to the primary. */
    color?: string;
}

export function RateGauge({
    percentage,
    size = "default",
    label,
    color,
}: RateGaugeProps) {
    if (percentage !== null && (percentage < 0 || percentage > 100)) {
        throw new Error("RateGauge: percentage must be between 0 and 100");
    }

    const { box, thickness } = RateGaugeGeometry[size];
    const center = box / 2;
    const radius = center - thickness / 2;
    const circumference = 2 * Math.PI * radius;

    // A rounded cap still paints a stub at zero, so drop the arc entirely.
    const hasArc = percentage !== null && percentage > 0;

    const value = percentage === null ? EMPTY_VALUE : `${percentage}%`;

    return (
        <div className={theme.container}>
            <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`}>
                <circle
                    className={theme.track}
                    cx={center}
                    cy={center}
                    r={radius}
                    strokeWidth={thickness}
                />
                {hasArc && (
                    <circle
                        className={cx(
                            theme.arc,
                            theme.arcSweep[size],
                            color === undefined && theme.arcDefaultColor
                        )}
                        cx={center}
                        cy={center}
                        r={radius}
                        stroke={color}
                        strokeWidth={thickness}
                        strokeDasharray={circumference}
                        strokeDashoffset={
                            circumference * (1 - percentage / 100)
                        }
                        transform={`rotate(${START_ANGLE} ${center} ${center})`}
                    />
                )}
                {size !== "xs" && (
                    <RateGaugeCenter
                        box={box}
                        size={size}
                        value={value}
                        label={label}
                    />
                )}
            </svg>
            {size === "xs" && <span>{value}</span>}
        </div>
    );
}

interface RateGaugeCenterProps {
    box: number;
    size: Exclude<RateGaugeSize, "xs">;
    value: string;
    label?: string;
}

function RateGaugeCenter({ box, size, value, label }: RateGaugeCenterProps) {
    const center = box / 2;
    const offsets = TEXT_OFFSETS[size];
    const valueY = label === undefined ? center : center + offsets.value;

    return (
        <>
            <text
                x={center}
                y={valueY}
                className={cx(theme.valueText, theme.valueTextSize[size])}
            >
                {value}
            </text>
            {label !== undefined && (
                <text
                    x={center}
                    y={center + offsets.label}
                    className={cx(theme.labelText, theme.labelTextSize[size])}
                >
                    {label}
                </text>
            )}
        </>
    );
}
